import { FiGift } from 'react-icons/fi';

import { FeatureLock } from '@/components/FeatureLock';
import { TIER_LABEL } from '@/pages/SubscriptionSettingsPage/subscription-display';
import { lockReasonOf, plansLabel } from '@/permissions/lock-copy';
import type { GateReason, PermissionGates } from '@/permissions/permission-gates';
import { type PermissionCategory, PERMISSIONS } from '@/permissions/permissions.catalog';

import { getCategoryMeta, getPermissionMeta, hasPermissionMeta } from '../../permissions.ui';
import PermissionRow from '../PermissionRow/PermissionRow';

import s from './PermissionGroupCard.module.scss';

interface Props {
  category: PermissionCategory;
  permissions: ReadonlySet<string>;
  excluded: ReadonlySet<string>;
  onToggle: (name: string, enabled: boolean) => void;
  search?: string;
  gates: PermissionGates;
  /** Opens the plans and seats; omitted when there is nowhere to upgrade, and no CTA shows. */
  onUpgrade?: () => void;
  /**
   * The vessel is on a lifetime plan an admin granted, which cannot be upgraded: the strip
   * says so in place of the upgrade button.
   */
  planGranted?: boolean;
  /** Why a row is read-only, used as its hover text; "All"/"None" reaches only the rest. */
  lockedReasonFor?: (name: string) => string | undefined;
}

export default function PermissionGroupCard({
  category,
  permissions,
  excluded,
  onToggle,
  search,
  gates,
  onUpgrade,
  planGranted = false,
  lockedReasonFor,
}: Props) {
  const query = search?.trim().toLowerCase() ?? '';
  const names = Object.values(PERMISSIONS[category])
    .filter((n) => !excluded.has(n))
    .filter((n) => hasPermissionMeta(n))
    .filter((n) => {
      if (!query) return true;
      const meta = getPermissionMeta(n);
      return (
        n.toLowerCase().includes(query) ||
        meta.title.toLowerCase().includes(query) ||
        meta.description.toLowerCase().includes(query)
      );
    });
  if (names.length === 0) return null;

  /**
   * Gated rows are left out of the count and out of "All"/"None" both: the server refuses
   * to change them, so counting them would make the tally read as something the owner
   * could act on. `x/y` is therefore "of what you can control here".
   */
  const open = names.filter((n) => !gates.reasonFor(n));
  const planLocked = names.filter(
    (n) => gates.reasonFor(n) === 'plan' && gates.unlockableByUpgrade(n),
  );
  const seatLocked = names.filter((n) => gates.reasonFor(n) === 'seat');
  const enabledCount = open.filter((n) => permissions.has(n)).length;
  const editable = open.filter((n) => !lockedReasonFor?.(n));
  const allEnabled = editable.every((n) => permissions.has(n));
  const meta = getCategoryMeta(category);
  // Something here is behind the plan and there is somewhere to buy it: every lock label offers it.
  const canUnlock = planLocked.length > 0 && !planGranted && !!onUpgrade;
  // Seats are reassigned where the plan is bought, and a lifetime plan's seats cannot be.
  const canMoveSeat = seatLocked.length > 0 && !planGranted && !!onUpgrade;
  const currentPlan = gates.planTier ? TIER_LABEL[gates.planTier] : 'current';

  /** The reason and its CTA for a set of gated permissions — one row, or the whole card. */
  const lockFor = (feature: string, gated: string[], reason: GateReason) => {
    const unlockable = reason === 'seat' ? canMoveSeat : canUnlock;
    return (
      <FeatureLock
        variant="inline"
        title={feature}
        reason={reason}
        seatOf="member"
        planName={currentPlan}
        requiredPlan={reason === 'plan' ? plansLabel(gates.plansCarrying(gated)) : ''}
        planGranted={planGranted}
        upgradeHref={null}
        onAction={unlockable ? onUpgrade : undefined}
      />
    );
  };
  const cardReason = lockReasonOf(gates, names);

  const handleToggleAll = () => {
    const target = !allEnabled;
    editable.forEach((n) => {
      if (permissions.has(n) !== target) onToggle(n, target);
    });
  };

  return (
    <div className={s.card}>
      <div className={s.header}>
        <div className={s.titleBlock}>
          <span className={s.title}>{meta.title}</span>
          <span className={s.description}>{meta.description}</span>
        </div>
        {open.length === 0 && cardReason ? (
          lockFor(meta.title, cardReason === 'seat' ? seatLocked : planLocked, cardReason)
        ) : (
          <div className={s.headerActions}>
            <span className={s.count}>
              {enabledCount}/{open.length}
            </span>
            {editable.length > 0 && (
              <button
                type="button"
                className={s.toggleAll}
                onClick={handleToggleAll}
                aria-label={allEnabled ? 'Disable All' : 'Enable All'}
              >
                {allEnabled ? 'None' : 'All'}
              </button>
            )}
          </div>
        )}
      </div>
      <div className={s.rows}>
        {names.map((name) => {
          const gate = gates.reasonFor(name);
          return (
            <PermissionRow
              key={name}
              name={name}
              enabled={permissions.has(name)}
              gate={gate}
              lock={gate && lockFor(getPermissionMeta(name).title, [name], gate)}
              lockedReason={lockedReasonFor?.(name)}
              onChange={(enabled) => onToggle(name, enabled)}
            />
          );
        })}
      </div>
      {planLocked.length > 0 && planGranted && (
        <div className={s.upgradeStrip}>
          <span className={s.upgradeText}>
            {open.length === 0
              ? `${meta.title} isn’t part of your lifetime plan, and you can’t upgrade a lifetime plan.`
              : `${planLocked.length} more ${meta.title} ${
                  planLocked.length === 1 ? 'permission isn’t' : 'permissions aren’t'
                } part of your lifetime plan, and a lifetime plan can’t be upgraded`}
          </span>
          <span className={s.planChip}>
            <FiGift className={s.planChipIcon} />
            Lifetime Plan
          </span>
        </div>
      )}
    </div>
  );
}
